import React, { useState, useEffect, useRef } from 'react';
import { 
  Button,
  Col,
  Form,
  Row,
  Table,
  Spinner,
  Pagination,
  Badge,
  InputGroup,
  Modal,
  Card
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from '../../api/axios';
import debounce from 'lodash.debounce';
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import { ArrowLeft, Download, FileEarmarkSpreadsheet, Printer, Search } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import DatePicker from 'react-datepicker';
import { useNavigate } from 'react-router-dom';

const UserCouponReport = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();
  
  const accessToken = localStorage.getItem("authToken");
  const [usersList, setUsersList] = useState([]);
  const animatedComponents = makeAnimated();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false);
  const [cachedUsersList, setCachedUsersList] = useState([]);
  const [userCouponData, setUserCouponData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [printBtnLoading, setPrintBtnLoading] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

  const latestSearch = useRef("");

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      borderRadius: '6px',
      minWidth: "200px",
      borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
      "&:hover": {
        borderColor: state.isFocused ? "#80bdff" : "#ced4da",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d"
    }),
  };

  const getCustomerData = async (query) => {
    latestSearch.current = query;
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const url = `user/getUsersWithOrders?search=${query}`;
      const response = await axios.get(url, { headers });

      if (latestSearch.current !== query) return;

      const users = response?.data?.data?.usersWithOrders || [];
      setUsersList(users);
      setCachedUsersList(users);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    let errorMessage = 'An error occurred';
    if (error.response?.status === 406) {
      errorMessage = error.response?.data?.error?.message || error.response?.data?.error;
    } else if (error.response?.status === 412) {
      errorMessage = error.response?.data?.data?.message;
    } else if (error.response?.status === 500) {
      errorMessage = error.response?.data?.error;
    }

    toast.error(errorMessage, {
      position: "top-right",
      autoClose: 3000,
      theme: "colored",
    });
  };

  const handleSearchChange = debounce(async (query) => { 
    if (query.trim().length > 0) {
      try {
        await getCustomerData(query);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    }
  }, 500);

  const handleDownloadExcel = async () => {
      setDownloadBtnLoading(true);
      getUserCouponReport(currentPage, recordsPerPage, selectedUser, true);
  };

  const getUserCouponReport = async (page, limit, selectedUser, download) => {
    setLoading(true);
    try {
      const headers = { 
        Authorization: `Bearer ${accessToken}`,
        "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      };
      let url = `order/coupons-used-by-user?page=${page}&limit=${limit}&user_id=${selectedUser}`;

      if(download) {
        url += `&isDownload=${download}`
      }

      const response = await axios.get(url, { 
        headers,
        responseType: download ? 'blob' : 'json'
      });

      if (download) {
          // Create a blob from the response data
          const blob = new Blob([response.data], { 
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          
          // Create a link element
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          
          // Set the filename for the download
          const fileName = `${selectedUserName} 's User Coupon Report.xlsx`;
          link.download = fileName;
          
          // Append the link to the body and trigger the download
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
  
          toast.success('Excel file downloaded successfully!', {
              position: "top-right",
              autoClose: 3000,
              theme: "colored"
          });
      } else {
        setUserCouponData(response?.data?.data?.data);
        setTotalPages(response?.data?.data?.pagination?.total_pages || 1);
        
        toast.success("Data Fetched Successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
      setDownloadBtnLoading(false);
    }
  }

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      getUserCouponReport(page, recordsPerPage, selectedUser);
    }
  }

  const handleSubmit = async(e) => {
    e.preventDefault();
    setSubmitBtnLoading(true);
    setClicked(true);
    await getUserCouponReport(currentPage, recordsPerPage, selectedUser);
    setSubmitBtnLoading(false);
  }

  const handlePrintReport = () => {
    const tableContent = `
      <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            <th style="border: 1px solid #dee2e6; padding: 8px;">#</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Customer Name</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Contact Details</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Ordered Date</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Order No</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Order Status</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Coupon Code</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Discount Amount</th>
            <th style="border: 1px solid #dee2e6; padding: 8px;">Grand Total</th>
          </tr>
        </thead>
        <tbody>
          ${userCouponData?.length > 0 ? 
            userCouponData?.map((customerInfo, index) => `
              <tr>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customerInfo?.user?.name}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">
                  ${customerInfo?.user?.email || 'N/A'}<br/>
                  ${customerInfo?.user?.mobile || 'N/A'}
                </td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customerInfo?.orderDate}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customerInfo?.order_sequence}${customerInfo?.orderNo}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px;">${customerInfo?.orderStatus}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; font-weight: bold; color: #28a745;">${customerInfo?.couponCode || 'N/A'}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${customerInfo?.discountAmount?.toFixed(2)}</td>
                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">₹ ${customerInfo?.grandTotal?.toFixed(2)}</td>
              </tr>
            `).join("") :
            `<tr>
              <td colspan="9" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No Customers Found</td>
            </tr>`
          }
        </tbody>
      </table>
    `;
  
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
        <head>
          <title>USER COUPON REPORT</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .title { font-size: 18px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; }
            th { background-color: #f8f9fa; text-align: left; }
          </style>
        </head>
        <body onload="window.print();window.close()">
          <div class="header">
            <div class="title">User Coupon Report</div>
          </div>
          ${tableContent}
        </body>
      </html>
    `);
    win.document.close();
  }

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage, endPage;

    if (totalPages <= maxVisiblePages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
      const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
      
      if (currentPage <= maxVisibleBeforeCurrent) {
        startPage = 1;
        endPage = maxVisiblePages;
      } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
        startPage = totalPages - maxVisiblePages + 1;
        endPage = totalPages;
      } else {
        startPage = currentPage - maxVisibleBeforeCurrent;
        endPage = currentPage + maxVisibleAfterCurrent;
      }
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item 
          key={totalPages} 
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"}`}>
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
              <div>
                  <h4 className="mb-0 fw-bold d-inline-block">
                      <i className="bi bi-ticket-perforated me-2"></i>USER COUPON REPORT
                  </h4>
              </div>
              <div>
                  <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => navigate('/reports/all-reports')}
                      className="me-3 d-flex align-items-center fw-semibold"
                      style={{ height: '38px' }}
                  >
                      <ArrowLeft className="me-1" /> Back to Reports
                  </Button>
              </div>
          </div>
          <hr className='mt-0 mb-0' /> 
          
          <div className="card-body">
            {/* Filters Section */}
            <div className="p-3 bg-light rounded-3">
              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group controlId="userSelect">
                      <Form.Label className="fw-semibold">
                        <i className="bi bi-person me-2"></i>Select User
                      </Form.Label>
                      <Select
                        options={usersList?.length > 0 ? usersList.map((user) => ({
                          value: user?._id,
                          label: `${user?.name} - ${user?.mobile}`,
                        })) : []}
                        components={animatedComponents}
                        styles={customStyles}
                        value={
                          selectedUser
                            ? (() => {
                                const user = cachedUsersList?.find(user => user?._id === selectedUser);
                                return user
                                  ? { value: selectedUser, label: `${user?.name} - ${user?.mobile}` }
                                  : { value: selectedUser, label: "Loading..." };
                              })()
                            : null
                        }
                        onInputChange={(query) => {
                          setSearchTerm(query);
                          handleSearchChange(query);
                        }}
                        onChange={(selectedOption) => {
                          setSelectedUser(selectedOption ? selectedOption?.value : null);
                          setSelectedUserName(selectedOption?.label)
                        }}
                        classNamePrefix="react-select"
                        isClearable
                        placeholder="Search and Select User"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} className="d-flex align-items-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 fw-semibold"
                      disabled={!selectedUser}
                    >
                      {submitBtnLoading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        "Generate Report"
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>

            {/* Results Section */}
            {!clicked ? (
              <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                <div className="text-center">
                  <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                  <h5 className="text-muted mt-3">
                    Please select a user to view coupon usage report
                  </h5>
                </div>
              </div>
            ) : (
              <>
                {/* Pagination and Records Per Page - Top */}
                <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                  <div className="d-flex align-items-center">
                    <span className="me-2 text-muted">Show</span>
                    <Form.Select
                      style={{ width: 'auto' }}
                      value={recordsPerPage}
                      onChange={(e) => {
                        const newRecordsPerPage = Number(e.target.value);
                        setRecordsPerPage(newRecordsPerPage);
                        setCurrentPage(1);
                        getUserCouponReport(1, newRecordsPerPage, selectedUser);
                      }}
                      className="border-secondary"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </Form.Select>
                    <span className="ms-2 text-muted">entries</span>
                  </div>
                  
                  <div className="d-flex align-items-center">
                    <Button
                      variant="outline-success"
                      className="me-3"
                      onClick={handleDownloadExcel}
                      disabled={!userCouponData.length}
                    >
                      <FileEarmarkSpreadsheet className="me-2" />
                      { downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel' }
                    </Button>
                    
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {renderPaginationItems()}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                </div>

                {/* User Coupon Data Table */}
                <div className="table-responsive border rounded-3">
                  <Table striped bordered hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th width="50">#</th>
                        <th>Customer Name</th>
                        <th>Contact Details</th>
                        <th width="120">Ordered Date</th>
                        <th width="120">Order No</th>
                        <th width="120">Order Status</th>
                        <th width="120">Coupon Code</th>
                        <th width="120" className="text-end">Discount Amount</th>
                        <th width="120" className="text-end">Grand Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 mb-0">Loading coupon data...</p>
                          </td>
                        </tr>
                      ) : userCouponData?.length > 0 ? (
                        userCouponData.map((customerData, index) => (
                          <tr key={index}>
                            <td className="text-muted">
                              {(currentPage - 1) * recordsPerPage + index + 1}
                            </td>
                            <td>
                              <div className="fw-semibold">{customerData?.user?.name || '-'}</div>
                            </td>
                            <td>
                              <div className="text-primary">{customerData?.user?.mobile || '-'}</div>
                              <div className="text-muted small">{customerData?.user?.email || 'No email'}</div>
                            </td>
                            <td>
                              <small>{customerData?.orderDate || '-'}</small>
                            </td>
                            <td>
                              <small>{customerData?.order_sequence}{customerData?.orderNo}</small>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  customerData.orderStatus === "COMPLETED"
                                    ? "success"
                                    : customerData.orderStatus === "DELIVERED"
                                    ? "primary"
                                    : customerData.orderStatus === "CONFIRMED"
                                    ? "secondary"
                                    : customerData.orderStatus === "READY-TO-PICK"
                                    ? "warning"
                                    : customerData.orderStatus === "CANCELLED"
                                    ? "danger"
                                    : "dark"
                                }
                                className="py-1 px-2"
                              >
                                {customerData.orderStatus}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="success" pill>
                                {customerData?.couponCode || '-'}
                              </Badge>
                            </td>
                            <td className="text-end">
                              ₹ {customerData?.discountAmount?.toFixed(2)}
                            </td>
                            <td className="text-end fw-semibold text-success">
                              ₹ {customerData?.grandTotal?.toFixed(2)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                            <p className="mt-2 mb-0 text-muted">
                              No coupon data found for the selected user
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination and Records Per Page - Bottom */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                    {Math.min(currentPage * recordsPerPage, userCouponData?.length)} of{' '}
                    {userCouponData?.length} entries
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default UserCouponReport;